import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { extractCountryAndCity, formatDate, formatMetric } from '../../../../../utils/util';
import { NOT_SURE } from '../../../../../utils/constants';

import Dropdown from '../../../../common/inputs/dropdown/Dropdown';
import TextInput from '../../../../common/inputs/text-input/TextInput';
import DatePicker from '../../../../common/inputs/date-picker/DatePicker';
import FilePicker from '../../../../common/inputs/file-picker/FilerPicker';
import Image from '../../../../common/image/Image';
import Button from '../../../../common/button/Button';
import Modal from '../../../../common/modal/Modal';
import ImageCarousel from '../../../../common/image-carousel/ImageCarousel';

import { ReactComponent as TrashIcon } from '../../../../../resources/icons/trash.svg';
import { ReactComponent as VerifiedIcon } from '../../../../../resources/icons/verified.svg';
import { ReactComponent as NotVerifiedIcon } from '../../../../../resources/icons/not-verified.svg';
import { ReactComponent as OpenLink } from '../../../../../resources/icons/open-new.svg';

import './buyer-listing-management.scss';

const BuyerListingManagement = ({
  listing,
  classifications,
  categories,
  types,
  metricUnits,
  retrieveCategories,
  retrieveTypes,
  retrieveMetricUnits,
  updateBuyerListing,
  sendForAmendment,
  approveProduct,
  showErrorToast,
  navigateToAccount,
  currencyCode,
}) => {
  const [classification, setClassification] = useState(undefined);
  const [category, setCategory] = useState(undefined);
  const [type, setType] = useState(undefined);
  const [brandName, setBrandName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [metric, setMetric] = useState(undefined);
  const [price, setPrice] = useState('');
  const [buyingPeriodFrom, setBuyingPeriodFrom] = useState(undefined);
  const [buyingPeriodTo, setBuyingPeriodTo] = useState(undefined);
  const [originCountry, setOriginCountry] = useState('');
  const [originCity, setOriginCity] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [destinationCity, setDestinationCity] = useState('');
  const [images, setImages] = useState([]);
  const [additionalInformation, setAdditionalInformation] = useState('');
  const [errors, setErrors] = useState({});
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [comment, setComment] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(undefined);

  const fileRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!listing) return;

    const { classification } = listing;
    setClassification(classification);

    retrieveCategories(classification.externalReference);
  }, [listing, retrieveCategories]);

  useEffect(() => {
    if (!listing) return;

    const { category } = listing;
    setCategory(category);
    retrieveTypes(category.externalReference);
  }, [listing, retrieveTypes]);

  useEffect(() => {
    if (!listing) return;

    const { specification } = listing;
    setType(specification);
    retrieveMetricUnits(specification.externalReference);
  }, [listing, retrieveMetricUnits]);

  useEffect(() => {
    if (!listing) return;

    setQuantity(listing.desiredQuantity ?? '');
    setMetric({ label: listing.desiredQuantityMetric, value: listing.desiredQuantityMetric });

    setBrandName(listing.brandName ?? '');
    setPrice(parseFloat(listing.preferredPrice?.split(' ')[0] ?? '').toFixed(2));
    setBuyingPeriodFrom(listing.preferredBuyingPeriodFrom ? new Date(listing.preferredBuyingPeriodFrom) : undefined);
    setBuyingPeriodTo(listing.preferredBuyingPeriodTo ? new Date(listing.preferredBuyingPeriodTo) : undefined);

    const [originCountry, originCity] = extractCountryAndCity(listing.preferredSourceLocation);
    setOriginCountry(originCountry);
    setOriginCity(originCity);

    const [destinationCountry, destinationCity] = extractCountryAndCity(listing.destinationLocation);
    setDestinationCountry(destinationCountry);
    setDestinationCity(destinationCity);

    setImages(listing.linkedMediaExternalReferences ?? []);
    setAdditionalInformation(listing.additionalInformation ?? '');
  }, [listing]);

  const handleClassificationChange = (classification) => {
    setClassification(classification);
    retrieveCategories(classification.externalReference);

    if (classification.externalReference === NOT_SURE) {
      handleCategoryChange(classification);
      return;
    }

    setCategory(null);
    setType(null);
    setMetric(null);
  };

  const handleCategoryChange = (category) => {
    setCategory(category);
    retrieveTypes(category.externalReference);

    if (category.externalReference === NOT_SURE) {
      handleTypeChange(category);
      return;
    }

    setType(null);
    setMetric(null);
  };

  const handleTypeChange = (type) => {
    setType(type);
    retrieveMetricUnits(type.externalReference);
    setMetric(null);
  };

  const handleFileChange = (file) => {
    setImages([...images, file]);
  };

  const handleDeleteImageClick = (event, index) => {
    event.stopPropagation();
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const hideAmendmentModal = () => {
    setComment('');
    setErrors((errors) => ({ ...errors, comment: false }));
    setShowAmendmentModal(false);
  };

  const handleSendClick = () => {
    if (!comment) {
      setErrors((errors) => ({ ...errors, comment: true }));
      return;
    }
    sendForAmendment(comment, hideAmendmentModal, true);
  };

  const validateFields = () => {
    const errors = {};

    if (!classification) errors.classification = true;
    if (!category) errors.category = true;
    if (!type) errors.type = true;
    if (!quantity) errors.quantity = true;
    if (!metric) errors.metric = true;
    if (!price) errors.price = true;
    if (!buyingPeriodFrom) errors.buyingPeriodFrom = true;
    if (!buyingPeriodTo) errors.buyingPeriodTo = true;
    if (!originCountry) errors.originCountry = true;
    if (!originCity) errors.originCity = true;
    if (!destinationCountry) errors.destinationCountry = true;
    if (!destinationCity) errors.destinationCity = true;

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleApproveClick = () => {
    if (!listing?.accountStatus || listing?.accountStatus !== 'Verified') {
      showErrorToast('Cannot approve a product of an unverified business');
      return;
    }

    handleSaveClick(false, approveProduct);
  };

  const handleSaveClick = (showToaster, onSuccess) => {
    const valid = validateFields();
    if (!valid) return;

    let productSpecificationExternalReference = null;

    if (type.externalReference !== NOT_SURE) {
      productSpecificationExternalReference = type.externalReference;
    }

    const buyerListing = {
      desiredQuantity: quantity,
      desiredQuantityMetric: metric?.value,
      desiredPricePerProductUnit: price,
      preferredBuyingPeriodFrom: buyingPeriodFrom,
      preferredBuyingPeriodTo: buyingPeriodTo,
      preferredOriginCountry: originCountry,
      preferredOriginCity: originCity,
      photos: images,
      productClassificationName: classification.name,
      productCategoryName: category.name,
      productSpecificationName: type.name,
      productSpecificationExternalReference,
      destinationCountry,
      destinationCity,
      brandName,
      additionalInformation,
    };

    updateBuyerListing(buyerListing, showToaster, onSuccess);
  };

  const renderImages = () => {
    return images.map((image, index) => {
      const key = typeof image === 'string' ? image : image.name;
      return (
        <div
          key={key}
          className="buyer-listing-management__product__form__photo-container"
          onClick={() => setSelectedImageIndex(index)}
        >
          {typeof image === 'string' ? (
            <Image className="buyer-listing-management__product__form__photo" externalReference={image} alt="" />
          ) : (
            <Image className="buyer-listing-management__product__form__photo" file={image} alt="" />
          )}
          <TrashIcon onClick={(event) => handleDeleteImageClick(event, index)} data-testid="delete-image-button" />
        </div>
      );
    });
  };

  return (
    <>
      <section className="buyer-listing-management">
        <div className="buyer-listing-management__float">
          <h1 className="buyer-listing-management__title">Buyer Listing</h1>
          <button className="buyer-listing-management__back-button" onClick={() => navigate(-1)}>
            {'<'} Back
          </button>
        </div>
        <section className="buyer-listing-management__product">
          <section className="buyer-listing-management__product__information">
            <h1>{listing?.brandName ? listing.brandName : listing?.specification?.name}</h1>

            <section className="buyer-listing-management__product__information__grid">
              <div className="buyer-listing-management__product__information__grid__item">
                <p>Last Modified By</p>
                <p>{listing?.lastModifiedBy?.name}</p>
              </div>
              <div className="buyer-listing-management__product__information__grid__item">
                <p>Current Status</p>
                <p className="buyer-listing-management__product__information__status">{listing?.status}</p>
              </div>
              <div className="buyer-listing-management__product__information__grid__item">
                <p>Account</p>
                <p>{listing?.account}</p>
              </div>
              <div className="buyer-listing-management__product__information__grid__item">
                <p>Account Status</p>
                <div className="buyer-listing-management__product__information__account-status">
                  <p>{listing?.accountStatus}</p>
                  {listing?.accountStatus === 'Verified' ? <VerifiedIcon /> : <NotVerifiedIcon />}
                  <OpenLink
                    className="buyer-listing-management__product__information__account-status__link"
                    onClick={() => {
                      if (listing?.accountExternalReference) {
                        navigateToAccount(listing?.accountExternalReference);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="buyer-listing-management__product__information__grid__item">
                <p>Uploaded By</p>
                <p>{listing?.uploadedBy?.name}</p>
              </div>
              <span />
              <div className="buyer-listing-management__product__information__grid__item">
                <p>Listing Created</p>
                <p>{formatDate(listing?.creationDate)}</p>
              </div>
            </section>
          </section>
          <section className="buyer-listing-management__product__form">
            <h2>Product Details</h2>

            <label htmlFor="product-classification">Product Classification</label>
            <Dropdown
              name="product-classification"
              value={classification}
              options={classifications}
              onChange={handleClassificationChange}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.classification}
              noSort
            />

            <label htmlFor="product-category">Product Category</label>
            <Dropdown
              name="product-category"
              value={category}
              options={categories}
              onChange={handleCategoryChange}
              disabled={!classification}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.category}
              noSort
            />

            <label htmlFor="product-type">Product Type</label>
            <Dropdown
              name="product-type"
              value={type}
              options={types}
              onChange={handleTypeChange}
              disabled={!category}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.type}
              noSort
            />

            <label htmlFor="product-name">Product Brand Name</label>
            <TextInput
              name="product-name"
              placeholder="Leave blank if product is generic"
              value={brandName}
              onChange={({ target }) => setBrandName(target.value)}
            />

            <section className="buyer-listing-management__product__form__grid">
              <section className="buyer-listing-management__product__form__grid__item">
                <label htmlFor="product-quantity">Desired Quantity is</label>
                <div>
                  <TextInput
                    name="product-quantity"
                    value={quantity}
                    onChange={({ target }) => setQuantity(target.value)}
                    error={errors.quantity}
                    numberOnly
                  />
                  <Dropdown
                    value={metric}
                    options={metricUnits}
                    onChange={(option) => setMetric(option)}
                    disabled={!type}
                    error={errors.metric}
                  />
                </div>
              </section>

              <section className="buyer-listing-management__product__form__grid__item">
                <label htmlFor="product-price">Desired Price per Product Unit is</label>
                <div>
                  <TextInput
                    name="product-price"
                    value={price}
                    onChange={({ target }) => setPrice(target.value)}
                    error={errors.price}
                    numberOnly
                  />
                  <p>
                    {currencyCode}
                    {metric ? ` per ${formatMetric(metric?.label, 1)}.` : '.'}
                  </p>
                </div>
                {currencyCode !== 'USD' && (
                  <div className="buyer-listing-management__price-disclaimer">
                    <p className="buyer-listing-management__price-disclaimer__text">*</p>
                    <p className="buyer-listing-management__price-disclaimer__text">
                      Please note that while you have selected to view all prices in {currencyCode}, all prices are
                      recorded in USD as per the exchange rate at the time of uploading this buyer listing.
                    </p>
                  </div>
                )}
              </section>

              <section className="buyer-listing-management__product__form__grid__item">
                <label htmlFor="product-buying-period-from">Preferred Buying Period From</label>
                <DatePicker
                  name="product-buying-period-from"
                  value={buyingPeriodFrom}
                  onChange={(date) => setBuyingPeriodFrom(date)}
                  error={errors.buyingPeriodFrom}
                />
              </section>

              <section className="buyer-listing-management__product__form__grid__item">
                <label htmlFor="product-buying-period-to">Preferred Buying Period To</label>
                <DatePicker
                  name="product-buying-period-to"
                  value={buyingPeriodTo}
                  onChange={(date) => setBuyingPeriodTo(date)}
                  error={errors.buyingPeriodTo}
                />
              </section>

              <section className="buyer-listing-management__product__form__grid__item">
                <label htmlFor="product-origin-country">Preferred Origin Country</label>
                <TextInput
                  name="product-origin-country"
                  value={originCountry}
                  onChange={({ target }) => setOriginCountry(target.value)}
                  error={errors.originCountry}
                />
              </section>

              <section className="buyer-listing-management__product__form__grid__item">
                <label htmlFor="product-origin-city">Preferred Origin City</label>
                <TextInput
                  name="product-origin-city"
                  value={originCity}
                  onChange={({ target }) => setOriginCity(target.value)}
                  error={errors.originCity}
                />
              </section>

              <section className="buyer-listing-management__product__form__grid__item">
                <label htmlFor="product-destination-country">Destination Country</label>
                <TextInput
                  name="product-destination-country"
                  value={destinationCountry}
                  onChange={({ target }) => setDestinationCountry(target.value)}
                  error={errors.destinationCountry}
                />
              </section>

              <section className="buyer-listing-management__product__form__grid__item">
                <label htmlFor="product-destination-city">Destination City</label>
                <TextInput
                  name="product-destination-city"
                  value={destinationCity}
                  onChange={({ target }) => setDestinationCity(target.value)}
                  error={errors.destinationCity}
                />
              </section>
            </section>

            <section className="buyer-listing-management__product__form__photos">
              <p>Photos</p>
              <div>
                {renderImages()}
                <FilePicker
                  fileRef={fileRef}
                  enableFileLimit={true}
                  fileType="image/*"
                  onChange={({ target }) => handleFileChange(target.files[0])}
                  triggerElement={
                    <button
                      className="buyer-listing-management__product__form__file-button"
                      onClick={() => fileRef.current.click()}
                      data-testid="add-image-button"
                    />
                  }
                />
              </div>
            </section>

            <section className="buyer-listing-management__product__form__grid__item">
              <label htmlFor="product-information">Additional Information</label>
              <TextInput
                name="product-information"
                value={additionalInformation}
                onChange={({ target }) => setAdditionalInformation(target.value)}
                multiline
              />
            </section>

            <section className="buyer-listing-management__product__form__actions">
              <Button onClick={() => navigate(-1)} secondary>
                Cancel
              </Button>
              <Button onClick={() => setShowAmendmentModal(true)} secondary>
                Send for Amendment
              </Button>
              <Button onClick={() => handleSaveClick(true)} secondary={listing?.status === 'pending approval'}>
                Save Changes
              </Button>
              {(listing?.status === 'pending approval' || listing?.status === 'sent for amendment') && (
                <Button onClick={handleApproveClick}>Approve Product</Button>
              )}
            </section>
          </section>
        </section>
      </section>

      {selectedImageIndex !== undefined && (
        <ImageCarousel images={images} startIndex={selectedImageIndex} setStartIndex={setSelectedImageIndex} />
      )}

      <Modal show={showAmendmentModal} setShow={setShowAmendmentModal}>
        <section className="buyer-listing-management__amendment-modal">
          <h2>Sending to Member for Amendments</h2>
          <p>The member will receive a notification with any comments added below:</p>
          <TextInput
            name="amendment-comments"
            placeholder="Comments..."
            value={comment}
            onChange={({ target }) => setComment(target.value)}
            error={errors.comment}
            multiline
          />

          <section className="buyer-listing-management__amendment-modal__actions">
            <Button
              className="buyer-listing-management__amendment-modal__button"
              onClick={hideAmendmentModal}
              secondary
            >
              Cancel
            </Button>

            <Button className="buyer-listing-management__amendment-modal__button" onClick={handleSendClick}>
              Send
            </Button>
          </section>
        </section>
      </Modal>
    </>
  );
};

BuyerListingManagement.propTypes = {
  listing: PropTypes.shape({}),
  classifications: PropTypes.arrayOf(PropTypes.shape({})),
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  types: PropTypes.arrayOf(PropTypes.shape({})),
  metricUnits: PropTypes.arrayOf(PropTypes.shape({})),
  retrieveCategories: PropTypes.func.isRequired,
  retrieveTypes: PropTypes.func.isRequired,
  retrieveMetricUnits: PropTypes.func.isRequired,
  updateBuyerListing: PropTypes.func.isRequired,
  sendForAmendment: PropTypes.func.isRequired,
  approveProduct: PropTypes.func.isRequired,
  showErrorToast: PropTypes.func.isRequired,
  navigateToAccount: PropTypes.func.isRequired,
  currencyCode: PropTypes.string.isRequired,
};

BuyerListingManagement.defaultProps = {
  listing: undefined,
  classifications: [],
  categories: [],
  types: [],
  metricUnits: [],
};

export default BuyerListingManagement;
